import { useState , useEffect} from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { bluetoothHelper } from "@/utils/bluetoothHelper";
import { Bluetooth, Plug } from "lucide-react";

// Energy Zones
const energyZones = [
  "Warm Up",
  "En1 Moderate",
  "En3 Threshold",
  "Sp1 Best Average",
  "Sp2 Race Pace",
  "Sp3 Sprint",
  "Recovery",
  "Extra Rest",
  "Aerobic",
  "Endurance",
  "En2 Threshold"
];

export function ToolsPage() {
  const [isWaterPoloOpen, setIsWaterPoloOpen] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState<number | null>(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(bluetoothHelper.isConnected());
  const [deviceName, setDeviceName] = useState<string | null>(
    bluetoothHelper.getDeviceName()
  );
  const [isUnsupportedDialogOpen, setIsUnsupportedDialogOpen] =
    useState(false);

  // Load preferred energy zone from localStorage
  const storedZone =
    localStorage.getItem("preferred_energy_zone") || "Warm Up";
  const [selectedZone, setSelectedZone] = useState(storedZone);

  const handleEnergyZoneChange = (value: string) => {
    setSelectedZone(value);
    localStorage.setItem("preferred_energy_zone", value);
    toast.success("Energy Zone preference updated");
  };

  const handleConnect = async () => {
    try {
      if (!("bluetooth" in navigator)) {
        setIsUnsupportedDialogOpen(true);
        return;
      }

      setIsConnecting(true);
      const device = await bluetoothHelper.connect();
      if (device) {
        setDeviceName(device.name || "Unknown Device");
        setIsConnected(true);
        toast.success(`Connected to ${device.name}`);
      }
    } catch (error: any) {
      console.error("Connection failed:", error);
      toast.error("Failed to connect to device");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await bluetoothHelper.disconnect();
      setIsConnected(false);
      setDeviceName(null);
      toast.warning("Bluetooth device disconnected");
    } catch (error: any) {
      console.error("Disconnection failed:", error);
      toast.error("Failed to disconnect");
    }
  };

  const handleTestCommunication = async () => {
    try {
      if (!isConnected) {
        toast.warning("Device not connected");
        return;
      }

      await bluetoothHelper.sendString("PING");
      toast.success("Sent test message: PING");
    } catch (error: any) {
      console.error("Communication failed:", error);
      toast.error("Test communication failed");
    }
  };

useEffect(() => {
  if (!isTimerRunning || totalSeconds === null) return;

  if (totalSeconds <= 0) {
    setIsTimerRunning(false);
    return;
  }

  const interval = setInterval(() => {
    setTotalSeconds((prev) => (prev !== null ? prev - 1 : prev));
  }, 1000);

  return () => clearInterval(interval);
}, [isTimerRunning, totalSeconds]);

// const handleOpenWaterPolo = () => {
//   setMinutes(0);
//   setSeconds(0);
//   setTotalSeconds(null);
//   setIsTimerRunning(false);
//   setIsWaterPoloOpen(true);
// };

const handleStartTimer = () => {
  const total = minutes * 60 + seconds;
  if (total <= 0) {
    toast.error("Please select time");
    return;
  }
  setTotalSeconds(total);
  setIsTimerRunning(true);
};

const handleRestartTimer = () => {
  const total = minutes * 60 + seconds;
  setTotalSeconds(total);
  setIsTimerRunning(false);
};

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Row: Bluetooth Tools + Preferences */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

         {/* Bluetooth Tools */}
        <Card>
          <CardHeader>
            <CardTitle>Bluetooth Tools</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">

            {/* Icon and description */}
            <div className="flex flex-col items-center space-y-2">
              <Bluetooth className="h-10 w-10 text-blue-500" />
              <p className="text-sm text-muted-foreground text-center">
                Manage your Bluetooth connection for syncing the Swimnerd Clock.
              </p>
            </div>

            {/* Connection status + buttons */}
            <div className="flex flex-col items-center space-y-4">
              <div className="text-center">
                <p className="font-medium">
                  {isConnected ? (
                    <>
                      Connected to:{" "}
                      <span className="text-green-600 font-semibold">
                        {deviceName}
                      </span>
                    </>
                  ) : (
                    <span className="text-red-600">Not Connected</span>
                  )}
                </p>
              </div>

              {!isConnected ? (
                <Button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-48"
                  size="lg"
                >
                  {isConnecting ? "Connecting..." : "Connect Device"}
                </Button>
              ) : (
                <Button
                  onClick={handleDisconnect}
                  className="bg-red-600 hover:bg-red-700 text-white w-48"
                  size="lg"
                >
                  Disconnect
                </Button>
              )}

              <Button
                onClick={handleTestCommunication}
                disabled={!isConnected}
                variant="outline"
                className="w-48 flex items-center justify-center gap-2"
              >
                <Plug className="h-4 w-4" />
                Test Communication
              </Button>
            </div>

            {/* Usage Notes inside card */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-sm mb-2">Usage Notes</h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                <li>Connect your Swimnerd Clock once using this page.</li>
                <li>
                  After connection, go to <b>Training Sets</b> and use the{" "}
                  <b>Power On/Off</b> button to control the clock.
                </li>
                <li>
                  The connection will stay active until you refresh or disconnect.
                </li>
              </ul>
            </div>

          </CardContent>


          
        </Card>

        {/* Preferences */}
        <Card>
          <CardHeader>
            <CardTitle>Preferences</CardTitle>
          </CardHeader>

          <CardContent className="space-y-3">
          <label className="text-sm font-medium">Energy Zone</label>

          <select
            value={selectedZone}
            onChange={(e) => handleEnergyZoneChange(e.target.value)}
            className="w-full border rounded-md p-2 text-sm"
          >
            {energyZones.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
        </CardContent>
        </Card>

{/* <Card>
  <CardHeader>
    <CardTitle>Water Polo</CardTitle>
  </CardHeader>

  <CardContent className="space-y-4 flex flex-col items-center">

    <p className="text-sm text-muted-foreground text-center">
      Use a countdown timer for Water Polo training sessions.
    </p>

    <Button
      onClick={handleOpenWaterPolo}
      className="w-48"
      size="lg"
    >
      Open Timer
    </Button>

  </CardContent>
</Card> */}


      </div>


      {/* Unsupported Dialog */}
      <Dialog
        open={isUnsupportedDialogOpen}
        onOpenChange={setIsUnsupportedDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bluetooth Not Supported</DialogTitle>
            <DialogDescription>
              Your browser does not support Web Bluetooth. Please use{" "}
              <b>Google Chrome</b> or <b>Microsoft Edge</b> on desktop or
              Android.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setIsUnsupportedDialogOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

<Dialog open={isWaterPoloOpen} onOpenChange={setIsWaterPoloOpen}>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Water Polo Timer</DialogTitle>
      <DialogDescription>
        Select time and start countdown
      </DialogDescription>
    </DialogHeader>

    <div className="space-y-4">

      {/* Time selectors */}
      <div className="flex gap-4">
        <div className="flex flex-col w-1/2">
          <label className="text-sm font-medium">Minutes</label>
          <select
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            className="border rounded-md p-2"
          >
            {Array.from({ length: 11 }).map((_, i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col w-1/2">
          <label className="text-sm font-medium">Seconds</label>
          <select
            value={seconds}
            onChange={(e) => setSeconds(Number(e.target.value))}
            className="border rounded-md p-2"
          >
            {Array.from({ length: 60 }).map((_, i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Timer display */}
      <div className="text-center text-4xl font-bold">
        {totalSeconds !== null
          ? totalSeconds
          : minutes * 60 + seconds}
      </div>

      {/* Buttons */}
      <div className="flex justify-center gap-3">
        <Button onClick={handleStartTimer} disabled={isTimerRunning}>
          Start
        </Button>

        <Button variant="outline" onClick={handleRestartTimer}>
          Restart
        </Button>

        <Button variant="destructive" onClick={() => setIsWaterPoloOpen(false)}>
          Close
        </Button>
      </div>

    </div>
  </DialogContent>
</Dialog>


    </div>
  );
}